import { useState } from 'react';
import { User, Bell, Building2, Plug, ArrowLeft, Mail } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { DatabaseStatus } from '../dashboard/DatabaseStatus';
import { IntegrationsView } from './IntegrationsView';
import { UserProfileView } from './UserProfileView';
import { NotificationsSettingsView } from './NotificationsSettingsView';
import { UserManagementView } from './UserManagementView';
import { CompanyProfileView } from './CompanyProfileView';
import EmailRemindersView from './EmailRemindersView';

export function SettingsView() {
  const { profile } = useAuth();
  const [activeTab, setActiveTab] = useState<string | null>(null);
  
  // Get actual user role from profile
  const userRole = profile?.role || 'Dispatch'; // Default to Dispatch if no role found

  const settingsSections = [
    {
      id: 'profile',
      title: 'User Profile',
      description: 'Manage your personal information and preferences',
      icon: User,
      color: 'bg-purple-100 text-purple-600',
      allowedRoles: ['superadmin', 'admin', 'dispatch', 'Superadmin', 'Admin', 'Dispatch', 'Safety', 'Maintenance', 'Accounting', 'Tracking'],
    },
    {
      id: 'notifications',
      title: 'Notifications',
      description: 'Configure email and system notification preferences',
      icon: Bell,
      color: 'bg-orange-100 text-orange-600',
      allowedRoles: ['superadmin', 'admin', 'dispatch', 'Superadmin', 'Admin', 'Dispatch', 'Safety', 'Maintenance', 'Accounting', 'Tracking'],
    },
    {
      id: 'company',
      title: 'Company Profile',
      description: 'Manage your company information and preferences',
      icon: Building2,
      color: 'bg-blue-100 text-blue-600',
      allowedRoles: ['superadmin', 'admin', 'Superadmin', 'Admin'],
    },
    {
      id: 'users',
      title: 'User Management',
      description: 'Add, edit, and manage user accounts and permissions',
      icon: User,
      color: 'bg-purple-100 text-purple-600',
      allowedRoles: ['superadmin', 'admin', 'Superadmin', 'Admin'],
    },
    {
      id: 'integrations',
      title: 'Integrations',
      description: 'Connect Samsara GPS, ELD, and other third-party services',
      icon: Plug,
      color: 'bg-indigo-100 text-indigo-600',
      allowedRoles: ['superadmin', 'Superadmin'], // Only Superadmin can see API integrations
    },
    {
      id: 'email-reminders',
      title: 'Email Reminders',
      description: 'Choose which events and document expirations email you',
      icon: Mail,
      color: 'bg-emerald-100 text-emerald-600',
      allowedRoles: ['superadmin', 'admin', 'Superadmin', 'Admin', 'Dispatch', 'Dispatch Manager', 'Safety', 'Safety Manager', 'Maintenance', 'Accounting', 'Accounting Manager', 'Recruiting'],
    },
  ];

  // Filter sections based on user role
  const visibleSections = settingsSections.filter(section => 
    section.allowedRoles.includes(userRole)
  );

  // Handle active tab views
  if (activeTab === 'profile') {
    return (
      <div className="space-y-6">
        <button
          onClick={() => setActiveTab(null)}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 shadow-sm hover:shadow"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Settings
        </button>
        <UserProfileView />
      </div>
    );
  }

  if (activeTab === 'notifications') {
    return (
      <div className="space-y-6">
        <button
          onClick={() => setActiveTab(null)}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 shadow-sm hover:shadow"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Settings
        </button>
        <NotificationsSettingsView />
      </div>
    );
  }

  if (activeTab === 'users') {
    return (
      <div className="space-y-6">
        <button
          onClick={() => setActiveTab(null)}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 shadow-sm hover:shadow"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Settings
        </button>
        <UserManagementView />
      </div>
    );
  }

  if (activeTab === 'company') {
    return (
      <div className="space-y-6">
        <button
          onClick={() => setActiveTab(null)}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 shadow-sm hover:shadow"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Settings
        </button>
        <CompanyProfileView />
      </div>
    );
  }

  if (activeTab === 'integrations') {
    return (
      <div className="space-y-6">
        <button
          onClick={() => setActiveTab(null)}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 shadow-sm hover:shadow"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Settings
        </button>
        <IntegrationsView />
      </div>
    );
  }

  if (activeTab === 'email-reminders') {
    return (
      <div className="space-y-6">
        <button
          onClick={() => setActiveTab(null)}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 shadow-sm hover:shadow"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Settings
        </button>
        <EmailRemindersView />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl shadow-lg p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2">System Settings</h2>
            <p className="text-slate-300">
              Configure and customize your TMS to match your business needs
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-400">Your Role:</span>
            <span className="inline-flex px-4 py-2 rounded-full text-sm font-semibold bg-blue-600 text-white">
              {userRole}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {visibleSections.map((section) => {
          const Icon = section.icon;
          return (
            <div
              key={section.id}
              onClick={() => setActiveTab(section.id)}
              className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow cursor-pointer"
            >
              <div className="flex items-start gap-4">
                <div className={`${section.color} p-3 rounded-lg flex-shrink-0`}>
                  <Icon className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{section.title}</h3>
                  <p className="text-sm text-slate-600">{section.description}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Database Status - Superadmin Only */}
      {(userRole === 'superadmin' || userRole === 'Superadmin') && <DatabaseStatus />}

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-4">Quick Settings</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <p className="font-semibold text-slate-900">Email Notifications</p>
              <p className="text-sm text-slate-600">Receive email updates for important events</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" defaultChecked />
              <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <p className="font-semibold text-slate-900">Maintenance Alerts</p>
              <p className="text-sm text-slate-600">Get notified when maintenance is due</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" defaultChecked />
              <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}
